"use client"

import { useState, useEffect } from "react"
import { Link as RouterLink, useNavigate } from "react-router-dom"
import { Box, TextField, Typography, Link, InputAdornment, IconButton, Alert } from "@mui/material"
import { Visibility, VisibilityOff } from "@mui/icons-material"
import { useAuth } from "../../contexts/AuthContext"
import PrimaryButton from "../../components/brand/PrimaryButton"
import GradientText from "../../components/brand/GradientText"

const Login = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  })
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState("")
  const [notice] = useState(() => sessionStorage.getItem("auth_notice"))
  const [loading, setLoading] = useState(false)
  const { login } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    sessionStorage.removeItem("auth_notice")
  }, [])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError("")
    setLoading(true)

    try {
      await login(formData.email, formData.password)
      navigate("/dashboard")
    } catch (error) {
      setError(error.response?.data?.message || "Failed to login")
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box>
      <Box sx={{ mb: 4, textAlign: "center" }}>
        <GradientText variant="h4" component="h1" sx={{ mb: 1 }}>
          Welcome Back
        </GradientText>
        <Typography variant="body1" color="text.secondary">
          Log in to your TinyTrack account
        </Typography>
      </Box>

      {notice && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {notice}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label="Email Address"
          name="email"
          autoComplete="email"
          autoFocus
          value={formData.email}
          onChange={handleChange}
          sx={{ mb: 2 }}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          name="password"
          label="Password"
          type={showPassword ? "text" : "password"}
          id="password"
          autoComplete="current-password"
          value={formData.password}
          onChange={handleChange}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 3 }}
        />

        <PrimaryButton type="submit" fullWidth disabled={loading} sx={{ mb: 2 }}>
          {loading ? "Logging in..." : "Log In"}
        </PrimaryButton>

        <Box sx={{ textAlign: "center", mt: 2 }}>
          <Link component={RouterLink} to="/forgot-password" variant="body2">
            Forgot password?
          </Link>
        </Box>
      </Box>
    </Box>
  )
}

export default Login

